import { useState } from 'react';
import { Button, Icon, TierBadge } from '@garnet/ui';
import type { AdvIntake } from '../../data/types.js';
import { AdvisorEyebrow, AdvisorMark, Citations, Confidence } from '../../components/advisor.js';

/* Garnet · AI Advisor — Intake (the headline example, rendered fully).
   At onboarding intake the Advisor proposes requesting documents beyond the
   baseline; the operator accepts — which raises the requirements. */

type Phase = 'pending' | 'modify' | 'accepted' | 'rejected';

function IntakeMeta({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>{k}</div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 12.5, marginTop: 3, color: 'var(--ink)' }}>{v}</div>
    </div>
  );
}

export function Intake({ d }: { d: AdvIntake }) {
  const [items, setItems] = useState<Record<string, boolean>>(() => {
    const m: Record<string, boolean> = {};
    d.items.forEach((it) => {
      m[it.k] = it.on;
    });
    return m;
  });
  const [phase, setPhase] = useState<Phase>('pending');
  const chosen = d.items.filter((it) => !!items[it.k]);
  const toggle = (k: string) => setItems((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div className="adv-stage-inner">
      <div className="adv-h">
        <div className="eb">§ Onboarding intake · advisor</div>
        <h1>The intake recommendation</h1>
        <p>
          A new counterparty enters diligence. Before any check runs, the Advisor reads the intake and proposes what the
          baseline does not cover. It proposes; you raise the requirements.
        </p>
      </div>

      <div className="adv-principle">
        <span className="ap-mark">
          <AdvisorMark size={14} />
        </span>
        <span>
          The Advisor <b>advises</b>. It drafts this request and grounds it. Raising it onto the record is your action —
          nothing is requested until you accept.
        </span>
      </div>

      {/* the intake under assessment */}
      <div className="panel" style={{ padding: '20px 22px', marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', letterSpacing: '.06em' }}>
              new intake · awaiting requirements
            </div>
            <h2
              className="display"
              style={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-.02em', margin: '5px 0 0', lineHeight: 1.05 }}
            >
              {d.supplier}
            </h2>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--muted)', marginTop: 6 }}>
              {d.flag} {d.place} · {d.role}
            </div>
          </div>
          <TierBadge level={d.tier} />
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
            gap: 16,
            marginTop: 18,
            paddingTop: 16,
            borderTop: '1px solid var(--rule)',
          }}
        >
          <IntakeMeta k="device" v={d.device} />
          <IntakeMeta k="manufacturer" v={d.maker} />
          <IntakeMeta k="consignment" v={d.consignment} />
          <IntakeMeta k="control chain" v={d.controlChain} />
        </div>
      </div>

      {/* baseline — what intake requests by default */}
      <div className="adv-seclabel first">Baseline document set · requested on every intake</div>
      <div className="panel" style={{ padding: '6px 0' }}>
        {d.baseline.map((b, i) => (
          <div
            key={b.k}
            style={{
              display: 'grid',
              gridTemplateColumns: '20px 1fr auto',
              gap: 11,
              alignItems: 'center',
              padding: '10px 16px',
              borderBottom: i < d.baseline.length - 1 ? '1px solid var(--rule)' : 0,
            }}
          >
            <span style={{ color: 'var(--muted)' }}>
              <Icon name="check" size={15} />
            </span>
            <span style={{ fontSize: 13.5, color: 'var(--ink)' }}>{b.name}</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{b.note}</span>
          </div>
        ))}
      </div>

      {/* the advisor's recommendation */}
      <div className="adv-seclabel">Advisor recommendation</div>

      {phase === 'accepted' ? (
        <div className="adv-card resolved">
          <div className="adv-resolved-bar">
            <span className="rb-tag accepted">accepted</span>
            <span className="rb-body" style={{ width: '100%' }}>
              <b>
                {chosen.length} requirement{chosen.length === 1 ? '' : 's'} raised onto the intake
              </b>
              <br />
              <span className="rb-who">You · just now — routed to {d.supplier}. Withdraw any before it sends.</span>
              <div style={{ marginTop: 11, display: 'grid', gap: 6 }}>
                {chosen.map((it, i) => (
                  <div key={it.k} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5 }}>
                    <span
                      style={{
                        fontFamily: 'var(--mono)',
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--verified-ink)',
                        background: 'var(--verified-bg)',
                        border: '1px solid var(--verified)',
                        borderRadius: 2,
                        padding: '1px 6px',
                      }}
                    >
                      REQ-4471-{3 + i}
                    </span>
                    <span style={{ color: 'var(--ink)' }}>{it.name}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 13 }}>
                <Button variant="secondary" sm icon="file" onClick={() => setPhase('pending')}>
                  Undo · back to recommendation
                </Button>
              </div>
            </span>
          </div>
        </div>
      ) : phase === 'rejected' ? (
        <div className="adv-card resolved">
          <div className="adv-resolved-bar">
            <span className="rb-tag rejected">rejected</span>
            <span className="rb-body">
              <b className="rb-strike">{d.headline}</b>
              <br />
              <span className="rb-who">You · just now — dismissed. The baseline set proceeds unchanged.</span>
              <div style={{ marginTop: 10 }}>
                <Button variant="ghost" sm icon="refresh" onClick={() => setPhase('pending')}>
                  Restore recommendation
                </Button>
              </div>
            </span>
          </div>
        </div>
      ) : (
        <div className="adv-card">
          <div className="ac-head">
            <div style={{ minWidth: 0 }}>
              <AdvisorEyebrow of={'Class III · ' + d.flag + ' free zone · trust in chain'} />
              <h3 className="ac-title" style={{ marginTop: 8 }}>
                {d.headline}
              </h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 7, flex: 'none' }}>
              <span className="adv-state pending">pending</span>
              <Confidence level={d.confidence} />
            </div>
          </div>

          <p className="ac-rationale">{d.rationale}</p>

          <div className="ac-items">
            {d.items.map((it) => {
              const on = !!items[it.k];
              const editable = phase === 'modify';
              return (
                <div
                  key={it.k}
                  className={'ac-item' + (on ? ' on' : ' off')}
                  role="button"
                  tabIndex={0}
                  onClick={() => editable && toggle(it.k)}
                  onKeyDown={(e) => editable && e.key === 'Enter' && toggle(it.k)}
                  style={{ cursor: editable ? 'pointer' : 'default' }}
                >
                  <span className="ck">{on && <Icon name="check" size={13} />}</span>
                  <div style={{ minWidth: 0 }}>
                    <div className="it-name">
                      {it.name}
                      <span className="beyond">beyond baseline</span>
                    </div>
                    <div className="it-why">{it.why}</div>
                    <div className="it-cites">
                      <Citations cites={it.cites} />
                    </div>
                  </div>
                  <div style={{ flex: 'none', paddingTop: 1 }}>
                    <Confidence level={it.confidence} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ac-proposed" style={{ marginTop: 14 }}>
            <span className="pl">you act</span>
            <span className="pt">
              {phase === 'modify' ? (
                <>
                  Raising <code>{chosen.length}</code> of 4 — toggle items above, then raise.
                </>
              ) : (
                d.performResult
              )}
            </span>
          </div>

          {phase === 'modify' ? (
            <div className="ac-actions">
              <Button variant="primary" sm icon="check" disabled={chosen.length === 0} onClick={() => setPhase('accepted')}>
                Raise {chosen.length} requirement{chosen.length === 1 ? '' : 's'}
              </Button>
              <Button
                variant="ghost"
                sm
                onClick={() => {
                  const m: Record<string, boolean> = {};
                  d.items.forEach((it) => {
                    m[it.k] = it.on;
                  });
                  setItems(m);
                  setPhase('pending');
                }}
              >
                Cancel changes
              </Button>
            </div>
          ) : (
            <div className="ac-actions">
              <Button variant="primary" sm icon="check" onClick={() => setPhase('accepted')}>
                {d.performLabel}
              </Button>
              <Button variant="secondary" sm icon="exchange" onClick={() => setPhase('modify')}>
                Modify
              </Button>
              <Button variant="ghost" sm icon="x" onClick={() => setPhase('rejected')}>
                Reject
              </Button>
            </div>
          )}

          <div className="ac-foot">
            The advisor proposes these four. <b>You</b> choose which to raise — accept all, modify the set, or reject. The
            decision is recorded against your name.
          </div>
        </div>
      )}
    </div>
  );
}
